import type { CapabilityStatus } from "../types.ts";

export interface PlatformModule {
  id: string;
  n: string;
  name: string;
  status: CapabilityStatus;
  desc: string;
}

export interface PlatformLayer {
  id: string;
  letter: string;
  name: string;
  status: CapabilityStatus;
  desc: string;
}

export const MODULES: PlatformModule[] = [
  { id: "iam",            n: "01", name: "Hub-IAM / ABAC",    status: "live",    desc: "Attribute-based access control and identity management across all platform modules. Single sign-on, RBAC roles, and audit-grade session tracking." },
  { id: "doc_authoring",  n: "02", name: "Doc Authoring",     status: "mvp",     desc: "USDM-native AI co-authoring for clinical documents — protocol, CSR, IB, ICF. Every paragraph is traceable to source data or approved templates." },
  { id: "data_review",    n: "03", name: "Data Review",       status: "mvp",     desc: "Structured data cleaning and query resolution. AI surfaces discrepancies, triages with context-aware suggestions, and closes queries in one workflow." },
  { id: "doc_mgmt",       n: "04", name: "Doc Management",    status: "mvp",     desc: "eTMF-aligned document management with version control, metadata tagging, and lifecycle workflows from draft through regulatory archive." },
  { id: "clinical_ops",   n: "05", name: "Clinical Ops",      status: "dev",     desc: "Site and patient operations: enrollment tracking, visit scheduling, protocol deviation management, and real-time operational dashboards." },
  { id: "cdm_startup",    n: "06", name: "CDM Startup",       status: "mvp",     desc: "Rapid CDM onboarding — database build, edit-check authoring, UAT scripts, and go-live readiness checklist in a single guided workflow." },
  { id: "data_transform", n: "07", name: "Data Transform",    status: "dev",     desc: "CDISC SDTM and ADaM transformation pipelines. Map raw data to submission-ready datasets with lineage, diff review, and validator integration." },
  { id: "design_studio",  n: "08", name: "Design Studio",     status: "dev",     desc: "Visual protocol and study design builder. Drag-and-drop schedule of assessments, endpoint definition, and USDM export in one canvas." },
  { id: "pv",             n: "09", name: "Pharmacovigilance", status: "planned", desc: "Adverse event intake, MedDRA coding, signal detection, and expedited reporting workflows aligned to ICH E2B(R3) and FDA IND safety requirements." },
  { id: "nda",            n: "10", name: "NDA / CTD",         status: "planned", desc: "End-to-end NDA and CTD submission assembly. Automated eCTD packaging, reviewer audit trail, and direct agency gateway integration." },
];

export const LAYERS: PlatformLayer[] = [
  { id: "agent_space",   letter: "A", name: "Agent Space",            status: "mvp", desc: "Orchestrated multi-agent workflows that span modules — data cleaning agents, authoring agents, and regulatory review agents all coordinated from a single runtime." },
  { id: "knowledge",     letter: "B", name: "Knowledge Assistant",    status: "mvp", desc: "RAG-powered knowledge layer grounded in PubMed, ClinicalTrials.gov, CDISC libraries, and your organisation's own approved documents and SOPs." },
  { id: "bio_backbone",  letter: "C", name: "Translational Biology",  status: "mvp", desc: "Unified biological data backbone connecting genomics, biomarkers, and clinical endpoints — enabling cross-study signal detection and translational analytics." },
];

export type PlatformRef =
  | { kind: "mod"; id: string }
  | { kind: "layer"; id: string };

export function findPlatformRef(label: string): PlatformRef | null {
  const lc = label.toLowerCase();
  for (const m of MODULES) {
    const firstWord = m.name.toLowerCase().split(" ")[0];
    if (lc.includes(firstWord) || lc.includes(m.id.replace("_", " "))) {
      return { kind: "mod", id: m.id };
    }
  }
  for (const l of LAYERS) {
    const firstWord = l.name.toLowerCase().split(" ")[0];
    if (lc.includes(firstWord) || lc.includes(l.id.replace("_", " "))) {
      return { kind: "layer", id: l.id };
    }
  }
  return null;
}

